import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { array, bool, object } from "prop-types";
import { Button, Checkbox, Modal, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import DataTable from "../shared/DataTable/DataTable";
import { getColumnsFromDataSource } from "../shared/DataTable/utils";
import { isLoading as setLoading } from "../../redux/shared/sharedActions";
import GenericPageHeader from "../shared/GenericPageHeader/GenericPageHeader";
import { getTagColor } from "../../utils/getTagColor";
import SelectionGroupTag from "../shared/SelectionGroupTag";
import StateFilter from "../Contracts/ContractHeader/StateFilter";
import EditCampaignIcon from "./CampaignHeader/EditCampaignIcon";
import { useHasRoles } from "../../utils/checkAuth";
import { useUpdateCampaigns } from "../../api/campaigns";
import { useUpdateContract } from "../../api/contracts";
import { useProductPlatforms } from "../../api/productPlatforms";
import { displayDate, toCalendarDate } from "../../utils/dates";
import { getAmazonAsinUrl } from "../../utils/amazon";

export default function CampaignsTable({
  campaigns = [],
  isLoading,
  useFilter,
  showSelection = false,
  contract,
}) {
  const contractId = contract?.contractId;

  const history = useHistory();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("not_disqualified");

  let filteredCampaigns = campaigns;
  if (useFilter) {
    filteredCampaigns = campaigns.filter(campaign => {
      switch (filter) {
        case "not_disqualified":
          return campaign.state !== "disqualified";
        case "none":
          return true;
        default:
          return campaign.state === filter;
      }
    });
  }

  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading, dispatch]);

  const columns = [
    "productName",
    "state",
    "code",
    "startDate",
    "defaultPrice",
    "target",
    "totalOnboards",
    //states
    // "not_started",
    // "started",
    "done",
    "followed_up",
  ];

  const { data: productPlatforms } = useProductPlatforms({});
  const productPlatformsById = useMemo(() => {
    const map = {};
    (productPlatforms || []).forEach(platform => {
      map[platform.productPlatformId] = platform;
    });
    return map;
  }, [productPlatforms]);

  const isAuthorized = useHasRoles(["admin"]);
  // Leading edit-icon column, admin only
  if (isAuthorized) {
    columns.unshift("edit");
  }
  if (showSelection && isAuthorized) {
    // "Group" sits directly to the right of "Selection" per the design.
    columns.splice(3, 0, "selection", "selectionGroup");
  }

  // Default sort groups like selection groups together (so the color-coded tags
  // line up), then orders by effective start date within each group. This
  // mirrors the influencer-facing selection order (newest first, offset-adjusted).
  const oneHour = 1000 * 60 * 60;
  const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
    const groupA = (a.selectionGroup || "default").toLowerCase();
    const groupB = (b.selectionGroup || "default").toLowerCase();
    const groupCompare = groupA.localeCompare(groupB);
    if (groupCompare !== 0) return groupCompare;

    const dateA = a.startDate
      ? new Date(a.startDate).getTime() + (a.sortOffset || 0) * oneHour
      : 0;
    const dateB = b.startDate
      ? new Date(b.startDate).getTime() + (b.sortOffset || 0) * oneHour
      : 0;
    return dateB - dateA;
  });

  const allSelected = filteredCampaigns.every(c => c.selectionActive);
  const updateCampaigns = useUpdateCampaigns();
  const updateContract = useUpdateContract(contractId);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  const [pendingCampaignIds, setPendingCampaignIds] = useState([]);
  // Track whether our update click came from the "toggle all" button, in which
  // case we should update the contract start date as well as the campaign start
  // dates.
  const [shouldUpdateContract, setShouldUpdateContract] = useState(false);

  const updateCampaignsSelection = async ({
    campaignIds,
    selectionActive,
    startDate,
    shouldUpdateContract,
  }) => {
    const updates = {
      selectionActive,
    };

    if (startDate) {
      updates.startDate = startDate;
    }

    setCampaignsLoading(true);

    try {
      const promises = [updateCampaigns.mutate({ campaignIds, updates })];
      if (shouldUpdateContract) {
        promises.push(updateContract.mutate({ body: { startDate } }));
      }
      await Promise.all(promises);
    } finally {
      setCampaignsLoading(false);
      setPendingCampaignIds([]);
      setShouldUpdateContract(false);
    }
  };

  const toggleCampaigns = async ({
    campaignIds,
    selectionActive,
    fromToggleAll = false,
  }) => {
    // First, check if all the campaigns are inactive and have 0 onboards
    let showModal = true;
    const campaignsToToggle = campaigns.filter(c =>
      campaignIds.includes(c.campaignId)
    );
    for (const campaign of campaignsToToggle) {
      if (campaign.totalOnboards > 0 || campaign.selectionActive) {
        showModal = false;
      }
    }

    // Show our modal confirming we want to sync start dates
    if (showModal) {
      setPendingCampaignIds(campaignIds);
      setShouldUpdateContract(fromToggleAll);
      return;
    }

    // We didn't need to confirm, so we just toggle the campaigns
    updateCampaignsSelection({ campaignIds, selectionActive });
  };

  const toggleAllCampaigns = () =>
    toggleCampaigns({
      campaignIds: filteredCampaigns.map(c => c.campaignId),
      selectionActive: !allSelected,
      fromToggleAll: true,
    });

  const currentCalendarDate = toCalendarDate(new Date());

  return (
    <>
      {history.location.pathname.startsWith("/campaigns") && (
        <GenericPageHeader
          title="Campaigns"
          extra={<StateFilter filter={filter} setFilter={setFilter} />}
        />
      )}
      <DataTable
        size="small"
        style={{ marginBottom: "12px" }}
        dataSource={sortedCampaigns}
        rowKey="campaignId"
        pagination={false}
        columns={getColumnsFromDataSource(campaigns, columns, {
          edit: {
            title: "",
            width: 40,
            align: "center",
            render: (_, record) => (
              <EditCampaignIcon campaignId={record.campaignId} />
            ),
          },
          productName: {
            render: (text, record) => (
              <a href={`/#/campaigns/${record.campaignId}`}>{text}</a>
            ),
          },
          state: {
            render: text => <Tag color={getTagColor(text)}>{text}</Tag>,
          },
          selectionGroup: {
            title: "Group",
            render: group => <SelectionGroupTag group={group || "default"} />,
          },
          selection: {
            title: () => (
              <div style={{ display: "flex", gap: "6px" }}>
                <Checkbox
                  checked={allSelected}
                  onClick={toggleAllCampaigns}
                  disabled={campaignsLoading || contract?.isApplicants}
                />
                <Typography.Text>Selection</Typography.Text>
              </div>
            ),
            render: (_, campaign) => {
              const {
                selectionActive,
                rulesTable,
                featuredPriority,
                sortOffset,
              } = campaign;

              const isFeatured = Number.isInteger(featuredPriority);
              const hasOffset = Number.isInteger(sortOffset);

              let offsets = "";
              if (isFeatured) {
                offsets += featuredPriority;
              }
              if (hasOffset) {
                // if > 0, add a + sign, else if negative do not prefix
                offsets += ` ${sortOffset >= 0 ? "+" : ""}${sortOffset}`;
              }

              // color code rules table by quality
              const rulesTableTagStyle = { marginRight: 0 };
              const lowerTable = rulesTable?.toLowerCase() || "";
              if (lowerTable.includes("_top")) {
                rulesTableTagStyle.color = "darkgreen";
              } else if (lowerTable.includes("_low")) {
                rulesTableTagStyle.color = "darkred";
              }

              return (
                <div style={{ display: "flex", gap: "6px" }}>
                  <Checkbox
                    checked={selectionActive}
                    onClick={e => {
                      toggleCampaigns({
                        campaignIds: [campaign.campaignId],
                        selectionActive: !selectionActive,
                      });
                      e.stopPropagation();
                    }}
                    disabled={campaignsLoading || contract?.isApplicants}
                  />
                  <Tag
                    color={isFeatured ? "purple" : "default"}
                    style={rulesTableTagStyle} // dynamic style
                  >
                    {rulesTable}
                  </Tag>
                  {offsets && <Typography.Text>{offsets}</Typography.Text>}
                </div>
              );
            },
          },
          //set column props by column name
          startDate: {
            render: text => displayDate(text).format("MMM Do YYYY"),
          },
          code: {
            title: "ASIN",
            render: (text, record) => {
              const platform = productPlatformsById[record.productPlatformId];
              const url = getAmazonAsinUrl(platform, text);

              if (!url) {
                return text;
              }

              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                >
                  {text}
                </a>
              );
            },
          },
          totalOnboards: {
            title: (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                Onboards
                <Tooltip title="Hover a row's onboard count to see its not-interested count">
                  <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                </Tooltip>
              </span>
            ),
            render: (text, record) => (
              <Tooltip title={`Not interested: ${record.not_interested || 0}`}>
                <span>{text}</span>
              </Tooltip>
            ),
          },
          followed_up: {
            title: "CTR",
          },
          defaultPrice: {
            title: "Price",
            render: text => `$${text}`,
          },
        })}
        onRow={record => ({
          onClick: () => history.push(`/campaigns/${record.campaignId}`), // click row
          // onDoubleClick: event => {}, // double click row
          // onContextMenu: event => {}, // right button click row
          // onMouseEnter: event => {}, // mouse enter row
          // onMouseLeave: event => {} // mouse leave row
        })}
      />
      <Modal
        visible={pendingCampaignIds.length > 0}
        closable={false}
        title={`Sync start dates for ${
          shouldUpdateContract ? "contract" : "new campaigns"
        }`}
        footer={[
          <Button
            key="1"
            disabled={campaignsLoading}
            onClick={
              // On confirm modal cancel, we update the selected campaigns to active without
              // resetting the start date
              () =>
                updateCampaignsSelection({
                  campaignIds: pendingCampaignIds,
                  selectionActive: true,
                })
            }
          >
            Don't Update
          </Button>,
          <Button
            key="2"
            type="primary"
            onClick={
              // On confirm modal submit, we update the selected campaigns to active and
              // also reset the start date
              () =>
                updateCampaignsSelection({
                  campaignIds: pendingCampaignIds,
                  selectionActive: true,
                  startDate: currentCalendarDate,
                  shouldUpdateContract,
                })
            }
            loading={campaignsLoading}
          >
            Update
          </Button>,
        ]}
      >
        Do you want to update the start date for the selected campaigns to
        today, {moment().format("MMM Do YYYY")}?
        {shouldUpdateContract && (
          <b> This will also update the contract start date.</b>
        )}
      </Modal>
    </>
  );
}

CampaignsTable.propTypes = {
  campaigns: array,
  isLoading: bool,
  useFilter: bool,
  showSelection: bool,
  contract: object,
};
